import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { useAuthContext } from './use-auth-context.hook';
import AuthClientStore from '../services/auth-client.service';
import { disablePush, enablePush, getPushStatus, PushStatus } from '../services/web-push.service';

/**
 * Who "I turned push on" belongs to on this browser. Per account, so the next
 * person to sign in here is not subscribed on someone else's say-so.
 */
export function pushAccountKey(user: { _id?: string; email?: string } | undefined, isSuperAdmin: boolean): string | null {
    const id = user?._id || user?.email;
    if (!id) return null;
    return isSuperAdmin ? `sa:${id}` : String(id);
}

/** State and switch for "notify me on this device", shown in the bell. */
export const usePushNotifications = () => {
    const { t, i18n } = useTranslation();
    const { user, isSuperAdmin, sendAuthGuardedRequest } = useAuthContext();
    const accountKey = pushAccountKey(user, isSuperAdmin);
    // Ardealy support working inside a vendor's account never subscribes.
    const supportSession = AuthClientStore.isSupportSession();

    const [status, setStatus] = useState<PushStatus>('unsupported');
    const [busy, setBusy] = useState(false);

    useEffect(() => {
        if (!accountKey || supportSession) return;
        let cancelled = false;
        getPushStatus(sendAuthGuardedRequest, accountKey)
            .then((next) => !cancelled && setStatus(next))
            .catch(() => undefined);
        return () => {
            cancelled = true;
        };
        // sendAuthGuardedRequest is recreated every render; the account is what matters.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [accountKey, supportSession]);

    const setEnabled = useCallback(
        async (on: boolean) => {
            if (!accountKey) return;
            setBusy(true);
            try {
                setStatus(
                    on
                        ? await enablePush(sendAuthGuardedRequest, accountKey, i18n.language)
                        : await disablePush(sendAuthGuardedRequest, accountKey),
                );
            } catch (error) {
                console.error('Push notifications toggle failed:', error);
                if (on) toast.error(t('PUSH.FAILED'));
                setStatus(await getPushStatus(sendAuthGuardedRequest, accountKey).catch(() => 'off' as PushStatus));
            } finally {
                setBusy(false);
            }
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [accountKey, t, i18n.language],
    );

    return { status: supportSession ? ('unsupported' as PushStatus) : status, busy, setEnabled };
};
